import { TaskTemplate, TaskTemplateCall } from "@/lib/vibrato-api"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { VibratoClientContext } from "./contexts"

function CreateTaskTemplateCallModal({ show, onClose, contact }: { show: boolean; onClose: () => void; contact: any }) {
  const navigate = useNavigate()
  const vibratoClient = React.useContext(VibratoClientContext)
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([])
  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState<string>("")
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null | undefined>(null)
  const [parameterValues, setParameterValues] = useState<{ [key: string]: string }>({})
  const [message, setMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  const contactFields = useMemo(() => {
    if (contact == null) {
      return []
    }

    return Object.keys(contact.custom_fields_map).map((key) => ({
      key,
      value: contact.custom_fields_map[key]
    }))
  }, [contact])

  useEffect(() => {
    ;(async () => {
      try {
        const templates = await vibratoClient.getTaskTemplates()
        setTaskTemplates(templates)
        if (templates.length > 0) {
          setSelectedTemplateUuid(templates[0].uuid)
          setSelectedTemplate(templates[0])
          // Set default parameterValues based on matching field names
          const defaultValues: { [key: string]: string } = {}
          templates[0].taskProperties.forEach((property: any) => {
            const matchingField = contactFields.find((field) => field.key.toLowerCase() === property.name.toLowerCase())
            if (matchingField) {
              defaultValues[property.slug as string] = matchingField.value
            }
          })
          setParameterValues(defaultValues)
        }
      } catch (error) {
        console.error("Failed to fetch task templates list:", error)
      }
    })()
  }, [vibratoClient, contactFields])

  useEffect(() => {
    if (selectedTemplate) {
      const defaultValues: { [key: string]: string } = {}
      if (selectedTemplate.taskProperties != null) {
        selectedTemplate.taskProperties.forEach((property) => {
          const matchingField = contactFields.find((field) => field.key.toLowerCase() === property.name.toLowerCase())
          if (matchingField) {
            defaultValues[property.slug as string] = matchingField.value
          }
        })
      }
      setParameterValues(defaultValues)
    }
  }, [selectedTemplate, contactFields])

  const onCreateCall = async () => {
    console.log("Creating call with template:", selectedTemplate, selectedTemplateUuid)
    if (selectedTemplate == null || selectedTemplateUuid === "") {
      setErrorMessage("Please select a task template.")
      return
    }

    try {
      const taskTemplateCall: TaskTemplateCall = {
        taskTemplateUuid: selectedTemplateUuid,
        taskTemplateParameters: Object.entries(parameterValues).map(([slug, value]) => ({ slug, value })),
        contactUuid: contact.uuid
      }

      const call = await vibratoClient.createCallFromTaskTemplate(taskTemplateCall)
      setMessage("Call created successfully!")
      setErrorMessage("")
      await new Promise((resolve) => setTimeout(resolve, 1000))
      navigate(`/calls/${call.id}/edit`)
    } catch (error) {
      console.error("Failed to create call:", error)
      setMessage("")
      setErrorMessage("Error creating call.")
    }
  }

  return (
    <Modal show={show} onClose={onClose} className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        {contact?.first_name ? (
          <span className="text-purple-600">Create Task Template Call - {contact.first_name}</span>
        ) : (
          <span className="text-purple-600">Create Task Template Call</span>
        )}
      </ModalHeader>
      <ModalBody>
        <div>
          <label htmlFor="taskTemplate" className="block text-sm font-medium text-gray-700">
            Select Task Template
          </label>
          <select
            id="taskTemplate"
            value={selectedTemplateUuid}
            onChange={(e) => {
              setSelectedTemplateUuid(e.target.value)
              setSelectedTemplate(taskTemplates.find((template) => template.uuid === e.target.value))
            }}
            className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
          >
            {taskTemplates.map((template) => (
              <option key={template.uuid} value={template.uuid}>
                {template.taskName}
              </option>
            ))}
          </select>
          {selectedTemplate && (
            <div className="mt-2">
              <p className="text-sm text-gray-500">{selectedTemplate.taskDescription}</p>
            </div>
          )}
        </div>
        {selectedTemplate && selectedTemplate.taskProperties != null && selectedTemplate.taskProperties.length > 0 && (
          <>
            <hr className="mt-4" />
            <div className="mt-4">
              <p className="text-sm font-normal text-gray-500 mb-2">
                Please match the required task properties with the corresponding contact fields or provide a custom
                value.
              </p>
              <div className="flex flex-col gap-2">
                {taskTemplates
                  .find((template) => template.uuid === selectedTemplateUuid)
                  ?.taskProperties?.map((property) => (
                    <div key={property.slug} className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">{property.name}</label>
                      <p className="text-sm text-gray-500">{property.description}</p>
                      <div className="flex  justify-center items-center pt-2">
                        <select
                          className="block w-full mr-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
                          value={parameterValues[property.slug] || ""}
                          onChange={(e) => setParameterValues({ ...parameterValues, [property.slug]: e.target.value })}
                        >
                          <option value="">Select Contact Field</option>
                          {contactFields.map((field) => (
                            <option key={field.key} value={field.value}>
                              {field.key} ({field.value})
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
                          placeholder="Value"
                          value={parameterValues[property.slug] || ""}
                          onChange={(e) => setParameterValues({ ...parameterValues, [property.slug]: e.target.value })}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="flex flex-col gap-2">
          {!message && (
            <>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onCreateCall}
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                >
                  Create Call
                </button>
              </div>
            </>
          )}
          {errorMessage && <div className="text-red-500">{errorMessage}</div>}
          {message && <div className="text-green-500">{message}</div>}
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default CreateTaskTemplateCallModal
